import fb from '@app/firebase'
import store from '@app/state'
import {
  FBREF_MANUAL_SLOPE_TABLES,
  FBREF_MANUAL_SLOPE_TABLE,
} from '@app/evaluation-core/refs'

export type Tee = {
  title: string
  slopeRating: number
  courseRating: number
  color?: string
}

export interface SavedSlopeTable {
  id?: string
  tees: Tee[]
  par: number
  courseName: string
  clubName: string
  clubId: string
  isDraft: boolean
  gender: 'M' | 'W'
  ratingDate: string
  ratingName: string
  // The holes the table covers. Tables saved before the range could be picked
  // carry neither, and those are left alone rather than assumed to be 1-18
  startHole?: number
  endHole?: number
  createdBy: string
  createdAt: number
  updatedAt: number
}

export const createManualSlopeTable = async (
  clubId: string,
  data: Omit<SavedSlopeTable, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>
): Promise<string> => {
  const userId = store.getState().user.id
  const now = Date.now()
  const ref = await fb
    .database()
    .ref(FBREF_MANUAL_SLOPE_TABLES(clubId))
    .push({
      ...data,
      createdBy: String(userId),
      createdAt: now,
      updatedAt: now,
    })
  return ref.key!
}

export const getManualSlopeTable = async (
  clubId: string,
  id: string
): Promise<SavedSlopeTable | null> => {
  const snapshot = await fb
    .database()
    .ref(FBREF_MANUAL_SLOPE_TABLE(clubId, id))
    .get()
  const val = snapshot.val()
  return val ? { ...val, id } : null
}

export const getManualSlopeTablesForClub = (
  clubId: string,
  callback: (tables: SavedSlopeTable[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  // Without a club id the ref resolves to the manualSlopeTables root, which
  // would read every club's tables
  if (!clubId) {
    onError?.(new Error('Missing club id'))
    return () => undefined
  }

  const path = FBREF_MANUAL_SLOPE_TABLES(clubId)
  const ref = fb.database().ref(path)
  const handler = ref.on(
    'value',
    (snapshot) => {
      const val = snapshot.val()
      if (!val) {
        callback([])
        return
      }
      const tables = Object.entries(val).map(([key, data]) => ({
        ...(data as SavedSlopeTable),
        id: key,
      }))
      callback(tables)
    },
    // A cancelled listener is delivered here and nowhere else. Without this
    // argument a denied read is completely silent and the caller waits forever
    (error: Error) => {
      console.error(
        `[manualSlopeTableService] Listener for ${path} was cancelled`,
        { code: (error as any).code, message: error.message }
      )
      onError?.(error)
    }
  )
  return () => ref.off('value', handler)
}

export const updateManualSlopeTable = async (
  clubId: string,
  id: string,
  data: Partial<SavedSlopeTable>
): Promise<void> => {
  await fb
    .database()
    .ref(FBREF_MANUAL_SLOPE_TABLE(clubId, id))
    .update({
      ...data,
      updatedAt: Date.now(),
    })
}

export const deleteManualSlopeTable = async (
  clubId: string,
  id: string
): Promise<void> => {
  await fb.database().ref(FBREF_MANUAL_SLOPE_TABLE(clubId, id)).remove()
}
